// # Privacy Policy Page Component
import React, { useState, useEffect } from 'react';
import DashboardHeader from '../../components/DashboardHeader';
import Header from '../components/header';  // # Import regular header
import DashboardFooter from '../../components/DashboardFooter';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../auth/firebase';
import { useAuthStore } from '../../auth/state';
import cacheManager from '../../auth/cache_manager';
import './legal.css';

function Privacy() {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const { user, isAuthenticated } = useAuthStore();  // # Get user and auth state

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Add hero background style
  const heroStyle = {
    background: `linear-gradient(rgba(44, 62, 80, 0.7), rgba(44, 62, 80, 0.7)), url(${process.env.PUBLIC_URL + '/icons/policy.jpg'})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  };

  return (
    <div className={`legal_privacy-page ${isDarkMode ? 'legal_dark-mode' : ''}`}>
      {isAuthenticated ? (
        <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      ) : (
        <Header />
      )}
      
      <main className="legal_privacy-content">
        <section className="legal_hero-section" style={heroStyle}>
          <h1>Privacy Policy</h1>
          <p>How we collect, use, and protect your information</p>
        </section>

        <section className="legal_privacy-section">
          <div className="legal_privacy-container">
            <div className="legal_privacy-group">
              <h2>1. Information We Collect</h2>
              <div className="legal_privacy-item">
                <h3>1.1 Personal Information</h3>
                <p>We collect information you provide directly, including:</p>
                <ul>
                  <li>Name and contact information</li>
                  <li>Educational background</li>
                  <li>Payment information</li>
                  <li>Usage data and preferences</li>
                </ul>
              </div>
            </div>

            <div className="legal_privacy-group">
              <h2>2. How We Use Your Information</h2>
              <div className="legal_privacy-item">
                <h3>2.1 Service Provision</h3>
                <p>We use your information to:</p>
                <ul>
                  <li>Provide and personalize our services</li>
                  <li>Process payments and subscriptions</li>
                  <li>Send important updates and notifications</li>
                  <li>Improve our services and user experience</li>
                </ul>
              </div>
              <div className="legal_privacy-item">
                <h3>2.2 Research and Development</h3>
                <p>To improve medical education and our services:</p>
                <ul>
                  <li>We may use de-identified user data for research purposes</li>
                  <li>All personal identifiers are removed before research use</li>
                  <li>Research findings may be used to enhance educational content</li>
                  <li>Aggregated, anonymous data may be used in academic publications</li>
                </ul>
              </div>
            </div>

            <div className="legal_privacy-group">
              <h2>3. Information Security</h2>
              <div className="legal_privacy-item">
                <h3>3.1 Data Protection</h3>
                <p>We implement appropriate security measures to protect your information from unauthorized access, disclosure, or misuse.</p>
              </div>
            </div>

            <div className="legal_privacy-group">
              <h2>4. Your Rights</h2>
              <div className="legal_privacy-item">
                <p>You have the right to:</p>
                <ul>
                  <li>Access your personal information</li>
                  <li>Request corrections to your data</li>
                  <li>Request deletion of your data</li>
                  <li>Opt-out of marketing communications</li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      </main>

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default Privacy; 